import { fetchSenderProxyImports } from '@/api/admin';
import { Button } from '@/components/ui/button';
import type { SenderProxyImportRecord } from '@/entities/sender.entity';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import { proxyFormatLabel, proxyProtocolLabel } from './proxy-labels';

function formatDate(value: string): string {
	return new Date(value).toLocaleString();
}

export default function ProxyImportsTable({ refreshKey }: { refreshKey: number }) {
	const [items, setItems] = useState<SenderProxyImportRecord[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchSenderProxyImports({ page, limit: 10 });
			setItems(data.items);
			setTotal(data.total);
		} finally {
			setLoading(false);
		}
	}, [page]);

	useEffect(() => {
		void load();
	}, [load, refreshKey]);

	return (
		<div className='flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4'>
			<div className='flex items-center justify-between gap-2'>
				<p className='text-sm font-medium'>История импортов ({total})</p>
				<Button type='button' size='sm' variant='ghost' onClick={() => void load()}>
					Обновить
				</Button>
			</div>

			{loading && items.length === 0 ? <p className='text-xs text-muted-foreground'>Загрузка…</p> : null}
			{!loading && items.length === 0 ? <p className='text-xs text-muted-foreground'>Импортов пока нет</p> : null}

			<div className={cn('flex flex-col gap-2', loading && items.length > 0 && 'opacity-60 pointer-events-none')}>
				{items.map(item => (
					<div key={item.id} className='rounded-md border border-border bg-card px-3 py-2 text-xs'>
						<div className='flex flex-wrap items-center justify-between gap-2'>
							<span className='font-medium'>{item.filename}</span>
							<span className='text-muted-foreground'>{formatDate(item.createdAt)}</span>
						</div>
						<p className='text-muted-foreground mt-1'>
							{proxyProtocolLabel(item.protocol)} · {proxyFormatLabel(item.format)}
						</p>
						<p className='text-muted-foreground mt-1'>
							+{item.added} новых · обновлено {item.updated} · невалидных {item.invalidLines} · всего в пуле{' '}
							{item.proxyCountAfter}
						</p>
					</div>
				))}
			</div>

			{total > 10 ? (
				<div className='flex gap-2'>
					<Button type='button' size='sm' variant='outline' disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
						Назад
					</Button>
					<Button
						type='button'
						size='sm'
						variant='outline'
						disabled={page * 10 >= total}
						onClick={() => setPage(p => p + 1)}
					>
						Далее
					</Button>
				</div>
			) : null}
		</div>
	);
}
